//#region Imports
import { createFeature, createReducer, on } from '@ngrx/store';
import {
    AdminLoginData,
    AuthData, AuthDataState, AuthError, AuthErrorState, AuthState, Customer, OTPResponse, OTPStateData,
    PasswordData, PhoneNumberData, SSNData, UserData,
    UserInfoData
} from './../../interfaces';
import * as authDataActions from './auth-data.actions';

//#region State consts

const data: AuthData = {
    dbo: '',
    phoneNumber: '',
    rosekey: '',
    ssn: '',
    password: '',
    otp: '',
};

const authError: AuthErrorState = {
    error: null,
    error_description: null,
};

const userInfoData: UserInfoData = {
    dbo: '',
    email: '',
    email_verified: false,
    family_name: '',
    given_name: '',
    name: '',
    preferred_username: '',
    rosekey: '',
    ssn: '',
    sub: ''
}

const initialState: AuthDataState = {
    data,
    error: authError,
    isLoaded: false,
    isUserValid: null,
    isOTPValid: null,
    isLoading: false,
    isOTPLoading: false,
    isUpdatePasswordLoading: false,
    isUpdatePasswordSuccess: false,
    isUpdatePasswordError: false,
    accessToken: '',
    isCreatingUser: false,
    userInfoData: userInfoData,
    isAdmin: false
};

export const AuthDataFeatureName = 'AuthData';

const IsLoadedAuthDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isLoaded: !initialState.isLoaded });

const LoadAuthDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isLoaded: initialState.isLoaded });

const OTPActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isCreatingUser: false, isUserValid: null });

const RegisterUserActionFunction: any =
    (state: AuthDataState): AuthDataState => state;

const ResetAuthDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...initialState, isLoading: false });

const ResetAdminAuthDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...initialState, isLoading: false, isAdmin: true });

const ResetDBODataActionFunction: any =
    (state: AuthDataState) => ({ ...state, data: { ...state.data, dbo: data.dbo } });

const ResetAuthErrorActionFunction: any =
    (state: AuthDataState) => ({ ...state, error: authError });

const ResetOTPDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, data: { ...state.data, otp: data.otp }, isOTPValid: initialState.isOTPValid, accessToken: '' });

const ResetPasswordDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, data: { ...state.data, password: data.password } });

const ResetPhoneNumberDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, data: { ...state.data, phoneNumber: data.phoneNumber } });

const ResetRoseKeyDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, data: { ...state.data, rosekey: data.rosekey } });

const ResetSSNDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, data: { ...state.data, ssn: data.ssn } });

const SetAuthDataActionFunction: any =
    (state: AuthDataState, data: AuthData): AuthDataState => ({ ...state, data });

const SetAuthErrorActionFunction: any =
    (state: AuthDataState, { error, error_description }: AuthError): AuthDataState => ({ ...state, isLoading: false, error: { error, error_description }, isAdmin: false });

const SetDBODataActionFunction: any =
    (state: AuthDataState, { dbo }: any): AuthDataState => ({ ...state, data: { ...state.data, dbo } });

const SetIsUserValidActionFunction: any =
    (state: AuthDataState, { valid: isUserValid }: AuthState): AuthDataState => ({ ...state, isLoading: false, isUserValid });

// SNP-241: a valid code routes on whether the phone has an account. isUserValid true sends the OTP
// page to Set password (reset); false sends it to DOB / last-4 SSN, which then creates the account.
const SetIsOTPValidActionFunction: any =
    (state: AuthDataState, { otpData }: { otpData: OTPResponse }): AuthDataState => ({
        ...state,
        isOTPValid: true,
        isOTPLoading: false,
        accessToken: otpData.accessToken,
        isUserValid: otpData.userExists === true,
        isCreatingUser: otpData.userExists !== true,
    });

const SetIsOTPInvalidActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isOTPValid: false, isOTPLoading: false, accessToken: '' });

const SetLoggedDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isLoading: false });

const SetOTPDataActionFunction: any =
    (state: AuthDataState, { otp }: OTPStateData): AuthDataState => ({ ...state, isOTPLoading: true, data: { ...state.data, otp } });

const PostUserActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isLoading: true });

const PostUserSuccessActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isLoading: false, isOTPLoading: false, isUserValid: true });

const PostUserErrorActionFunction: any =
    (state: AuthDataState, { alreadyExists }: { alreadyExists: boolean }): AuthDataState => {
        const message = alreadyExists ? 'User already exists' : 'User not found';
        return { ...state, isLoading: false, isOTPLoading: false, isUserValid: false, error: { error: message, error_description: message } };
    };

const SetPasswordDataActionFunction: any =
    (state: AuthDataState, { password }: PasswordData): AuthDataState => ({ ...state, isLoading: true, data: { ...state.data, password } });

const SetPhoneNumberDataActionFunction: any =
    (state: AuthDataState, { phoneNumber }: PhoneNumberData): AuthDataState => ({ ...state, isLoading: true, data: { ...state.data, phoneNumber } });

const SetRoseKeyDataActionFunction: any =
    (state: AuthDataState, { rosekey }: any): AuthDataState => ({ ...state, data: { ...state.data, rosekey } });

const SetSSNDataActionFunction: any =
    (state: AuthDataState, { ssn }: SSNData): AuthDataState => ({ ...state, data: { ...state.data, ssn } });

const SetUserIdentityActionFunction: any =
    (state: AuthDataState, { userInfoData }: any): AuthDataState => ({
        ...state,
        data: { ...state.data, dbo: userInfoData.dbo, rosekey: userInfoData.rosekey, ssn: userInfoData.ssn },
        isLoaded: !initialState.isLoaded,
        userInfoData: { ...userInfoData }
    });

const ValidateLogedUserActionFunction: any =
    (state: AuthDataState) => state;

const PutPasswordDataActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isUpdatePasswordLoading: true, isUpdatePasswordSuccess: false, isUpdatePasswordError: false });

const PutPasswordDataSuccessActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isUpdatePasswordLoading: false, isUpdatePasswordSuccess: true, isUpdatePasswordError: false });

const PutPasswordDataErrorActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isUpdatePasswordLoading: false, isUpdatePasswordSuccess: false, isUpdatePasswordError: true });

const PutPasswordDataResetActionFunction: any =
    (state: AuthDataState): AuthDataState => ({ ...state, isUpdatePasswordLoading: false, isUpdatePasswordSuccess: false, isUpdatePasswordError: false });

const SetLoginDataActionFunction: any =
    (state: AuthDataState, { username, password }: AdminLoginData): AuthDataState => ({ ...state, isLoading: true, isAdmin: true, data: { ...state.data, phoneNumber: username, password } });

const SetIsAdminFunction: any =
    (state: AuthDataState, { isAdmin }: { isAdmin: boolean }): AuthDataState => ({ ...state, isAdmin });

const LoadCustomerDataFunction: any =
    (state: AuthDataState, {roseKey: rosekey, phoneNumber, fullName, firstName, lastName, email}: Customer): AuthDataState => (
        { ...state,
            isLoaded: false,
            isLoading: true,
            data: { ...state.data, rosekey, phoneNumber },
            userInfoData : { ...state.userInfoData, rosekey, email, name: fullName, preferred_username: phoneNumber, family_name: lastName, given_name: firstName, email_verified: true },
            isOTPValid: true,
            isUserValid: true,
        });
const RemoveCustomerDataFunction: any =
    (state: AuthDataState): AuthDataState => (
        { ...state,
            isLoaded: false,
            isLoading: false,
            data: { ...state.data, rosekey: '', phoneNumber: '' },
            userInfoData : { ...state.userInfoData, rosekey: '', email: '', name: '', preferred_username: '', family_name: '', given_name: '', email_verified: false },
            isOTPValid: null,
            isUserValid: null,
        });

const reducer = createReducer(
    initialState,
    on(authDataActions.IsLoadedAuthDataAction, IsLoadedAuthDataActionFunction),
    on(authDataActions.LoadAuthDataAction, LoadAuthDataActionFunction),
    on(authDataActions.OTPAction, OTPActionFunction),
    on(authDataActions.RegisterUserAction, RegisterUserActionFunction),
    on(authDataActions.ResetAuthDataAction, ResetAuthDataActionFunction),
    on(authDataActions.ResetDBODataAction, ResetDBODataActionFunction),
    on(authDataActions.ResetAuthErrorAction, ResetAuthErrorActionFunction),
    on(authDataActions.ResetOTPDataAction, ResetOTPDataActionFunction),
    on(authDataActions.ResetPasswordDataAction, ResetPasswordDataActionFunction),
    on(authDataActions.ResetPhoneNumberDataAction, ResetPhoneNumberDataActionFunction),
    on(authDataActions.ResetRoseKeyDataAction, ResetRoseKeyDataActionFunction),
    on(authDataActions.ResetSSNDataAction, ResetSSNDataActionFunction),
    on(authDataActions.SetAuthDataAction, SetAuthDataActionFunction),
    on(authDataActions.SetAuthErrorAction, SetAuthErrorActionFunction),
    on(authDataActions.SetDBODataAction, SetDBODataActionFunction),
    on(authDataActions.SetIsUserValidAction, SetIsUserValidActionFunction),
    on(authDataActions.SetIsOTPValidAction, SetIsOTPValidActionFunction),
    on(authDataActions.SetIsOtpInvalidAction, SetIsOTPInvalidActionFunction),
    on(authDataActions.SetLoggedDataAction, SetLoggedDataActionFunction),
    on(authDataActions.SetOTPDataAction, SetOTPDataActionFunction),
    on(authDataActions.PostUserAction, PostUserActionFunction),
    on(authDataActions.PostUserSuccessAction, PostUserSuccessActionFunction),
    on(authDataActions.PostUserErrorAction, PostUserErrorActionFunction),
    on(authDataActions.SetPasswordDataAction, SetPasswordDataActionFunction),
    on(authDataActions.SetPhoneNumberDataAction, SetPhoneNumberDataActionFunction),
    on(authDataActions.SetRoseKeyDataAction, SetRoseKeyDataActionFunction),
    on(authDataActions.SetSSNDataAction, SetSSNDataActionFunction),
    on(authDataActions.SetUserIdentityAction, SetUserIdentityActionFunction),
    on(authDataActions.ValidateLogedUserAction, ValidateLogedUserActionFunction),
    on(authDataActions.PutPasswordDataAction, PutPasswordDataActionFunction),
    on(authDataActions.PutPasswordDataSuccessAction, PutPasswordDataSuccessActionFunction),
    on(authDataActions.PutPasswordDataErrorAction, PutPasswordDataErrorActionFunction),
    on(authDataActions.PutPasswordDataResetAction, PutPasswordDataResetActionFunction),
    on(authDataActions.SetLoginDataAction, SetLoginDataActionFunction),
    on(authDataActions.SetIsAdmin, SetIsAdminFunction),
    on(authDataActions.LoadCustomerDataAction, LoadCustomerDataFunction),
    on(authDataActions.RemoveCustomerDataAction, RemoveCustomerDataFunction),
    on(authDataActions.ResetAdminAuthDataAction, ResetAdminAuthDataActionFunction)
);

//#region Feature

export const AuthDataFeature = createFeature({
    name: AuthDataFeatureName,
    reducer: reducer
});
